// note: qdtComponents must be loaded into the Vue prototype at app.$qdt
// in a project plugin. Look for qdt.config.js

export default async ({ app }, inject) => {
  const qdtComponents = app.$qdt
  const engine = await qdtComponents.qDocPromise

  /**
   * When SDP finds a real developer, remove this.
   */
  console.log(`
----------------------------------------------------------------------------------------------------------
    Welcome to the School District of Philadelphia fellow developer.

    If you know anyone that is interested in developing for the school district,
    please pass along this job posting:
    https://aa080.referrals.selectminds.com/CENTRALOFFICE/jobs/dashboard-developer-and-data-coach-683
    
    Alternatively (in case the link changes), an interested person can search the 
    central office job board at: https://aa080.referrals.selectminds.com/CENTRALOFFICE.
    
    Enjoy the website and please consider working with us - Jonathan Vitale
----------------------------------------------------------------------------------------------------------
  `)

  /// ////////////////////////////////// QLIK UTILITIY API ////////////////////////////

  const clearAll = async () => {
    const engine = await qdtComponents.qDocPromise
    await engine.clearAll()
    return true
  }

  const clearField = async fieldName => {
    const engine = await qdtComponents.qDocPromise
    const field = await engine.getField(fieldName)
    await field.clear()
    return true
  }

  const destroySessionObject = async sessionObject => {
    const engine = await qdtComponents.qDocPromise
    await engine.destroySessionObject(sessionObject.id)
    return true
  }

  const generateHypercubeObjectFromDef = async qDef => {
    const engine = await qdtComponents.qDocPromise
    const sessionObject = await engine.createSessionObject(qDef)
    return sessionObject
  }

  /**
   * Generates a hypercube with a single field and measure.
   * This is useful for filtering the field by the measure
   * @param {*} field The name of the field, will also be used for the label
   * @param {*} sortBy See Qlik Engine API for qSortCriterians format
   * @param {*} measureExpression The expression for the measure, the label will simply be 'measure'
   */
  const generateFieldMeasureHypercubeObjectFromDef = async (
    field,
    sortBy,
    measureExpression
  ) => {
    const sortCriterias = {}
    const criteria = Object.keys(sortBy)
    criteria.forEach(criterion => {
      if (criterion === 'state') {
        sortCriterias.qSortByState = sortBy[criterion]
      } else if (criterion === 'numeric' || criterion === 'number') {
        sortCriterias.qSortByNumeric = sortBy[criterion]
      } else if (criterion === 'text' || criterion === 'ascii') {
        sortCriterias.qSortByAscii = sortBy[criterion]
      }
    })

    const qDef = {
      qInfo: { qType: 'visualization' },
      qHyperCubeDef: {
        qDimensions: [
          {
            qDef: {
              qFieldDefs: [field],
              qFieldLabels: [field],
              qSortCriterias: [sortCriterias]
            },
            qNullSuppression: true
          }
        ],
        qMeasures: [
          {
            qDef: {
              qDef: measureExpression,
              qLabel: 'measure'
            }
          }
        ],
        qInitialDataFetch: [
          {
            qWidth: 2,
            qHeight: 5000
          }
        ]
      }
    }

    // create a session object that will respond to user selections, will use the update methods to get changes
    const engine = await qdtComponents.qDocPromise
    const object = await engine.createSessionObject(qDef)
    return object
  }

  const generateSessionLists = async () => {
    const qDef = {
      qInfo: { qType: 'SessionLists' },
      qSelectionObjectDef: {}
    }

    // create a session object that will respond to user selections, will use the update methods to get changes
    const engine = await qdtComponents.qDocPromise
    const object = await engine.createSessionObject(qDef)
    return object
  }

  /**
   *
   * @param {*} sessionsList
   * returns selectionsByField { field1: [value1, value2, ...], field2: [value1, value2, ...], ..}
   */
  const getCurrentSelectionsFromSessionLists = async sessionsList => {
    const layout = await sessionsList.getLayout()

    if (layout.qSelectionObject && layout.qSelectionObject.qSelections) {
      const qSelections = layout.qSelectionObject.qSelections
      // console.log("getCurrentSelectionsFromSessionLists", qSelections)
      const selectionsByField = {}
      qSelections.forEach(obj => {
        const values = obj.qSelectedFieldSelectionInfo.map(s => {
          return {
            text: s.qName,
            // number: , // I don't see this type of information
            isSelected: true,
            isExcluded: false
          }
        })
        selectionsByField[obj.qField] = values
      })
      return selectionsByField
    } else {
      return null
    }
  }

  /**
   * Creates a list object for a single field.
   * @param {String} field
   * @param {Object} sortBy See Qlik Engine API for qSortCriterians format
   */
  const generateListObject = async (field, sortBy, stateName) => {
    const sortCriterias = {}
    const criteria = Object.keys(sortBy)
    criteria.forEach(criterion => {
      if (criterion === 'state') {
        sortCriterias.qSortByState = sortBy[criterion]
      } else if (criterion === 'numeric' || criterion === 'number') {
        sortCriterias.qSortByNumeric = sortBy[criterion]
      } else if (criterion === 'text' || criterion === 'ascii') {
        sortCriterias.qSortByAscii = sortBy[criterion]
      }
    })

    const qDef = {
      qInfo: { qType: 'ListObject' },
      qListObjectDef: {
        qDef: {
          qFieldDefs: [field],
          qFieldLabels: [field],
          qSortCriterias: [sortCriterias]
        },
        qInitialDataFetch: [
          {
            qWidth: 1,
            qHeight: 5000
          }
        ],
        qShowAlternatives: true
      }
    }
    if (stateName) qDef.qStateName = stateName

    // create a session object that will respond to user selections, will use the update methods to get changes
    const engine = await qdtComponents.qDocPromise
    const object = await engine.createSessionObject(qDef)
    return object
  }

  const getFieldValueByIndex = (values, index, targetField) => {
    if (typeof index === 'number' && index >= 0) {
      if (values[targetField] && index < values[targetField].length) {
        return values[targetField][index]
      } else {
        return null
      }
    } else if (Array.isArray(index) && index.length) {
      return values[targetField].filter((val, i) => index.includes(i))
    } else {
      return null
    }
  }
  const getObjectFromId = async qId => {
    const engine = await qdtComponents.qDocPromise
    return await engine.getObject(qId)
  }

  /**
   * Creates a key-value object with the name of the dimension or measure as the key and the values in the
   * dimension or measure as an array value. Users should use another function from this API to lookup values.
   * @param {Object} hypercubeObject The Qlik hypercube object returned from a fulfilled call to generateHypercubeObjectFromDef
   */
  const getValuesFromHypercubeObject = async hypercubeObject => {
    const layout = await hypercubeObject.getLayout()

    const hcProps = {
      cols: {
        cols: layout.qHyperCube.qDimensionInfo.concat(
          layout.qHyperCube.qMeasureInfo
        )
      },
      width: layout.qHyperCube.qSize.qcx,
      height: Math.floor(10000 / layout.qHyperCube.qSize.qcx)
    }

    let fullMatrix = []
    let numReturnedrows = 0

    while (numReturnedrows < layout.qHyperCube.qSize.qcy) {
      const remData = layout.qHyperCube.qSize.qcy - numReturnedrows

      const fetchSize = remData < hcProps.height ? remData : hcProps.height

      let newData = await hypercubeObject.getHyperCubeData({
        qPath: '/qHyperCubeDef',
        qPages: [
          {
            qLeft: 0,
            qTop: numReturnedrows,
            qWidth: hcProps.width,
            qHeight: fetchSize
          }
        ]
      })

      newData = newData[0].qMatrix

      numReturnedrows += newData.length

      fullMatrix = fullMatrix.concat(newData)
    }
    /* TG 032423 - This was was duplicating hypercube data - probably an artifact of JV's old code to handle paginations that was succeeded by the above while loop
    layout.qHyperCube.qDataPages.forEach(obj => {
      fullMatrix = fullMatrix.concat(obj.qMatrix)
    }) */

    console.log('layout Data Pages:', layout.qHyperCube.qDataPages)
    console.log('fullMatrix:', fullMatrix)

    const valuesByField = {}
    let values, isNumeric

    // dimensions
    layout.qHyperCube.qDimensionInfo.forEach((dim, index) => {
      isNumeric =
        layout.qHyperCube.qDimensionInfo[index].qTags.filter(
          value => value.includes('numeric') || value.includes('integer')
        ).length > 0

      values = fullMatrix.map(value => {
        try {
          return {
            text: value[index].qText,
            number: isNumeric ? value[index].qNum : null,
            isSelected: value[index].qState === 'S',
            isExcluded: value[index].qState === 'X',
            state: value[index].qState
          }
        } catch (error) {
          console.log(
            'error accessing data with value',
            value,
            'index',
            index,
            error
          )
        }
      })
      valuesByField[dim.qFallbackTitle] = values
    })
    const ndims = layout.qHyperCube.qDimensionInfo.length

    // measures - note the inner loop maps across measures and dimensions so the index should skip dims
    layout.qHyperCube.qMeasureInfo.forEach((dim, index) => {
      const mindex = index + ndims
      values = fullMatrix.map(value => {
        try {
          return {
            text: value[mindex].qText,
            number: value[mindex] != null ? value[mindex].qNum : null,
            isSelected: value[mindex].qState === 'S',
            isExcluded: value[mindex].qState === 'X'
          }
        } catch (error) {
          console.log(
            'error accessing data with value',
            value,
            'index',
            mindex,
            error
          )
        }
      })
      valuesByField[dim.qFallbackTitle] = values
    })

    return valuesByField
  }

  /**
   *
   * @param {*} listObject
   */
  const getValuesFromListObject = async listObject => {
    const layout = await listObject.getLayout()

    let fullMatrix = []
    layout.qListObject.qDataPages.forEach(obj => {
      fullMatrix = fullMatrix.concat(obj.qMatrix.map(value => value[0]))
    })
    const isNumeric =
      layout.qListObject.qDimensionInfo.qTags.filter(
        value => value.includes('numeric') || value.includes('integer')
      ).length > 0
    const values = fullMatrix.map(value => ({
      text: value.qText,
      number: isNumeric ? value.qNum : null,
      isSelected: value.qState === 'S',
      isExcluded: value.qState === 'X'
    }))
    return values
  }

  const getVariableByName = async variableName => {
    return await engine.getVariableByName(variableName)
  }

  const getValueFromVariable = async variable => {
    const properties = await variable.getProperties()
    return properties.qDefinition
  }

  const setVariableValue = async (variable, value) => {
    const properties = await variable.getProperties()
    try {
      if (
        properties.qNumberPresentation.qType === 'U' ||
        properties.qNumberPresentation.qType === 'A'
      ) {
        await variable.setStringValue(value)
      } else {
        await variable.setNumValue(value)
      }
      return value
    } catch (error) {
      throw new Error('Error setting variable', variable, 'with value', value)
    }
  }

  /**
   * Returns the value of a variable stored in the Qlik Sense app.
   * @param {string} variableName The variable name inside the Qlik Sense app
   */
  const getVariableValueByName = async variableName => {
    const variable = await engine.getVariableByName(variableName)
    const properties = await variable.getProperties()
    return properties.qDefinition
  }

  /**
   * Returns the evaluated value of a variable stored in the Qlik Sense app.
   * returns object with qText, qNumber
   * @param {string} variableName The variable name inside the Qlik Sense app
   */
  const evaluateVariableValueByName = async variableName => {
    const variable = await engine.getVariableByName(variableName)
    const properties = await variable.getProperties()
    return await engine.evaluateEx(properties.qDefinition)
  }

  /**
   * Sets a variable with given value for the session.
   * @param {string} variableName The variable name inside the Qlik Sense app
   * @param {*} value The value that the variable will be set to
   */

  const setVariableValueByName = async (variableName, value) => {
    const variable = await engine.getVariableByName(variableName)
    const properties = await variable.getProperties()
    try {
      if (
        properties.qNumberPresentation.qType === 'U' ||
        properties.qNumberPresentation.qType === 'A'
      ) {
        await variable.setStringValue(value)
      } else {
        await variable.setNumValue(value)
      }
      return value
    } catch (error) {
      throw new Error(
        'Error setting variable',
        variableName,
        'with value',
        value
      )
    }
  }

  /**
   * This function provides a way to locate, in the values array, the position(s) that match a specific value in a field.
   * The returned indices can be used to get values from other fields.
   * For example, if we look up the values "Network 1" in a "Network" field, a set of indices will be returned
   * with all of the "rows" corresponding to Network 1. These indices can then be used by the function
   * "getValuesByIndex" to retrieve other values corresponding to these rows.
   * @param {object} values The fieldname-values array Object returned by getValuesFromHypercubeObject or getValuesFromListObject
   * @param {*} sourceField The name of the field to filter for specific values
   * @param {*} sourceFieldValue The specific values to match
   * @param {*} returnMultiple Allow multiple indices to be returned, if false (default), return first.
   *                            If true, return an array of all matching values.
   */
  const lookupIndexByFieldValue = (
    values,
    sourceField,
    sourceFieldValue,
    returnMultiple
  ) => {
    // console.log("lookupIndexByFieldValue", values, sourceField, sourceFieldValue)
    if (values && Object.keys(values).includes(sourceField)) {
      const fieldValues = values[sourceField]
      // add the index in the values object
      let fieldValuesIndex = fieldValues.map((val, index) => ({
        ...val,
        index
      }))
      // filter the source field to only the target value
      fieldValuesIndex = fieldValuesIndex.filter(
        val => val.text === sourceFieldValue || val.number === sourceFieldValue
      )
      if (fieldValuesIndex.length > 0) {
        if (returnMultiple) {
          return fieldValuesIndex.map(v => v.index)
        } else {
          return fieldValuesIndex[0].index
        }
      } else {
        // console.log("lookupIndexByFieldValue could not find index", values, sourceField, sourceFieldValue)
        return -1
      }
    } else {
      return -1
    }
  }

  /**
   * Given a set of indices representing the indices to be taken from each key with values,
   * return a single object per key.
   * @param {*} values
   * @param {*} index
   * @returns
   */
  const filterFieldValuesByIndex = (values, index) => {
    const newValues = {}
    Object.keys(values).forEach(key => {
      newValues[key] = values[key][index]
    })
    return newValues
  }

  /**
   * Given a set of indices representing the indices to be taken from each key with values,
   * return a filtered set of values.
   * @param {*} values
   * @param {*} indices
   * @returns
   */
  const filterFieldValuesByIndices = (values, indices) => {
    const newValues = {}
    Object.keys(values).forEach(key => {
      const fieldValues = values[key].filter(
        (f, index) => indices?.includes(index) || false
      )
      newValues[key] = fieldValues
    })
    return newValues
  }

  const formatReturnValues = (returnValues, returnKey) => {
    if (typeof returnKey === 'string') {
      if (Array.isArray(returnValues)) {
        if (Object.keys(returnValues[0]).includes(returnKey)) {
          return returnValues.map(v => v[returnKey])
        } else {
          return returnValues
        }
      } else if (Object.keys(returnValues).includes(returnKey)) {
        return returnValues[returnKey]
      } else {
        return returnValues
      }
    } else {
      return returnValues
    }
  }

  /**
   * Perform a lookup based upon a specific source field and value
   * (e.g., a specific school) and the target value to be returned
   * (e.g., the school's address).
   * @param {*} values The set of values to search
   * @param {*} sourceField The lookup field (e.g., School Name)
   * @param {*} sourceFieldValue The lookup value (e.g., "West Philadelphia HS")
   * @param {*} targetField The value to be returned (e.g., "Network 13")
   * @param {*} returnMultiple If multiple targetFields are found return multiple or first
   * @param {*} returnKey If left blank an object will be returned, if a key is given only values matching that key will be returned
   */
  const lookupValueByFieldValue = (
    values,
    sourceField,
    sourceFieldValue,
    targetField,
    returnMultiple,
    returnKey
  ) => {
    if (values != null) {
      const index = lookupIndexByFieldValue(
        values,
        sourceField,
        sourceFieldValue,
        returnMultiple
      )
      if (index >= 0 || index?.length > 0) {
        const returnValues = getFieldValueByIndex(values, index, targetField)
        return formatReturnValues(returnValues, returnKey)
      } else {
        return null
      }
    } else {
      return null
    }
  }

  const lookupIndexByMultipleFieldValues = (
    values,
    sourceFieldValueMap,
    returnMultiple
  ) => {
    // console.log('lookupIndexByMultipleFieldValues', values, sourceFieldValueMap)
    if (Object.keys(values).length > 0) {
      // we will filter the final indices from a set of all indices
      const length = values[Object.keys(values)[0]].length
      let finalIndices = [...Array(length).keys()]

      let sourceField, sourceFieldValue, fieldValues
      for (sourceField in sourceFieldValueMap) {
        sourceFieldValue = sourceFieldValueMap[sourceField]
        if (values[sourceField].length > 0) {
          fieldValues = values[sourceField]
          // add the index in the values object
          const fieldValuesIndex = fieldValues.map((val, index) => ({
            ...val,
            index
          }))
          // filter the source field to only the target value
          let indices = fieldValuesIndex.filter(val => {
            return (
              val.text === sourceFieldValue ||
              val.number === sourceFieldValue ||
              (val.number === 1 && sourceFieldValue === true)
            )
          })
          // console.log('indices', fieldValuesIndex, indices)
          indices = indices.map(val => val.index)

          // get the intersection of the indices selected from this field with the accumulated, final indices
          finalIndices = finalIndices.filter(val => indices.includes(val))
        }
      }
      if (finalIndices.length > 0) {
        if (returnMultiple) {
          return finalIndices
        } else {
          return finalIndices[0]
        }
      } else {
        // console.log("lookupIndexByFieldValue could not find index", values, sourceField, sourceFieldValue)
        return -1
      }
    } else {
      return -1
    }
  }

  const lookupValueByMultipleFieldValues = (
    values,
    sourceFieldValueMap,
    targetField,
    returnMultiple,
    returnKey
  ) => {
    if (values != null) {
      const index = lookupIndexByMultipleFieldValues(
        values,
        sourceFieldValueMap,
        returnMultiple
      )
      if (returnMultiple && index.length > 0) {
        const returnValues = index.map(i =>
          getFieldValueByIndex(values, i, targetField)
        )
        return formatReturnValues(returnValues, returnKey)
      } else if (!returnMultiple && index >= 0) {
        console.log(index, sourceFieldValueMap, targetField, values)
        const returnValues = getFieldValueByIndex(values, index, targetField)
        console.log(returnValues, sourceFieldValueMap, targetField, values)
        return formatReturnValues(returnValues, returnKey)
      } else {
        console.log(index, sourceFieldValueMap, targetField, values)
        return null
      }
    }
  }

  const selectFieldValues = async (fieldName, values) => {
    const f = value => {
      let obj = null
      if (typeof value === 'number') {
        obj = { qIsNumeric: true, qNumber: value }
      } else if (typeof value === 'string') {
        obj = { qIsNumeric: false, qText: value }
      } else if (typeof value.number === 'number') {
        obj = { qIsNumeric: true, qNumber: value.number }
      } else if (typeof value.text === 'string') {
        obj = { qIsNumeric: false, qText: value.text }
      }
      return obj
    }

    const engine = await qdtComponents.qDocPromise
    const field = await engine.getField(fieldName)
    const qValues = values.map ? values.map(value => f(value)) : [f(values)]

    if (qValues) {
      const response = await field.selectValues(qValues)
      if (response) {
        return values
      } else {
        return null
      }
    } else {
      return null
    }
  }

  /**
   * Toggle whether a particular value in a field is selected or not.
   * Works for strings, test for numeric/dual
   * @param {*} fieldName
   * @param {*} value
   */
  const toggleFieldValue = async (fieldName, value) => {
    const engine = await qdtComponents.qDocPromise
    const field = await engine.getField(fieldName)

    const response = await field.toggleSelect(value)
    if (response) {
      return value
    } else {
      return null
    }
  }

  const qlikAPI = {
    engine,
    clearAll,
    clearField,
    destroySessionObject,
    generateHypercubeObjectFromDef,
    generateFieldMeasureHypercubeObjectFromDef,
    generateListObject,
    generateSessionLists,
    getCurrentSelectionsFromSessionLists,
    getFieldValueByIndex,
    getObjectFromId,
    getValuesFromListObject,
    getValuesFromHypercubeObject,
    getVariableValueByName,
    evaluateVariableValueByName,
    lookupIndexByFieldValue,
    filterFieldValuesByIndex,
    filterFieldValuesByIndices,
    lookupValueByFieldValue,
    lookupIndexByMultipleFieldValues,
    lookupValueByMultipleFieldValues,
    selectFieldValues,
    toggleFieldValue,
    setVariableValueByName,
    getVariableByName,
    getValueFromVariable,
    setVariableValue
  }

  // Vue.prototype.$qlik = qlikAPI

  // app.$qlik = qlikAPI
  // console.log("app", app)
  inject('qlik', qlikAPI)
}
